<!-- AI Generation Loading Overlay -->
@if (isGenerating) {
  <div class="ai-loading-overlay">
    <div class="ai-loading-content">
      <div class="ai-loading-spinner">
        <div class="spinner-grow text-primary" role="status"></div>
        <div class="spinner-grow text-primary" role="status" style="animation-delay: 0.2s;"></div>
        <div class="spinner-grow text-primary" role="status" style="animation-delay: 0.4s;"></div>
      </div>
      <h3 class="mt-4 ai-loading-title">Setting up your workspace with AI</h3>
      <p class="ai-loading-message">{{ loadingMessage }}</p>
    </div>
  </div>
}

<!-- AI Generation Failed Overlay -->
@if (generationFailed && !isGenerating) {
  <div class="ai-loading-overlay">
    <div class="ai-loading-content text-center">
      <i class="bi bi-exclamation-triangle text-warning" style="font-size: 48px;"></i>
      <h3 class="mt-3">AI setup couldn't complete</h3>
      <p class="text-muted mb-4">We couldn't generate suggestions for your business. This can happen due to a temporary issue.</p>
      <div class="d-flex gap-3 justify-content-center">
        <button class="btn btn-primary" (click)="generateSuggestions()">
          <i class="bi bi-arrow-clockwise me-1"></i> Try Again
        </button>
        <button class="btn btn-outline-secondary" (click)="skipAiAndProceed()">
          Continue Manually
        </button>
      </div>
    </div>
  </div>
}

<div class="ws-container" [ngClass]="{'ws-dimmed': isGenerating, 'ws-no-sidebar': !showSidebar}">
  <!-- Left sidebar: step progress (hidden until org step + AI generation complete) -->
  @if (showSidebar) {
  <div class="ws-sidebar">
    <div class="ws-sidebar-header">
      <h4>Workspace Setup</h4>
      @if (currentStepKey !== 'finish') {
        <small class="text-muted">Step {{ currentStep }} of {{ steps.length - 2 }}</small>
      } @else {
        <small class="text-muted">Almost done!</small>
      }
    </div>

    <div class="ws-steps-list">
      @for (step of steps; track step.key; let i = $index) {
        @if (i > 0) {
        <div class="ws-step-item"
          [ngClass]="{
            'ws-step-active': i === currentStep,
            'ws-step-completed': stepStates[i].completed,
            'ws-step-skipped': stepStates[i].skipped,
            'ws-step-accessible': isStepAccessible(i),
            'ws-step-locked': !isStepAccessible(i)
          }"
          (click)="isStepAccessible(i) ? goToStep(i) : null">
          <div class="ws-step-indicator">
            @if (step.key === 'finish') {
              @if (stepStates[i].completed) {
                <i class="bi bi-check-circle-fill mr-2 text-success"></i>
              } @else {
                <i class="bi bi-flag-fill mr-2" [class.text-primary]="i === currentStep" [class.text-muted]="i !== currentStep"></i>
              }
            } @else if (stepStates[i].completed) {
              <i class="bi bi-check-circle-fill mr-2 text-success"></i>
            } @else if (stepStates[i].skipped) {
              <i class="bi bi-dash-circle mr-2 text-muted"></i>
            } @else if (i === currentStep) {
              <span class="ws-step-number active">{{ i }}</span>
            } @else {
              <span class="ws-step-number">{{ i }}</span>
            }
          </div>
          <div class="ws-step-label">
            <span>{{ step.label }}</span>
          </div>
        </div>
        }
      }
    </div>
  </div>
  }

  <!-- Right content: current step -->
  <div class="ws-content">
    <!-- Regenerating indicator -->
    @if (isRegenerating) {
      <div class="ws-regenerating-bar">
        <span class="spinner-border spinner-border-sm me-2"></span>
        AI is regenerating suggestions...
      </div>
    }

    @switch (currentStepKey) {
      @case ('account') {
        <ws-account-basics
          [activeAccount]="activeAccount"
          [settingUpTrialAccount]="settingUpTrialAccount"
          [prefillWebsiteUrl]="prefillWebsite"
          (completed)="onAccountBasicsCompleted($event)"
        />
      }
      @case ('callOutcomes') {
        <ws-call-outcomes
          [suggestions]="suggestions?.call_outcomes"
          [activeAccount]="activeAccount"
          (completed)="onStepCompleted(1)"
          (feedbackRequested)="onFeedbackRequested('call_outcomes', $event)"
        />
      }
      @case ('visitOutcomes') {
        <ws-visit-outcomes
          [suggestions]="suggestions?.visit_outcomes"
          [activeAccount]="activeAccount"
          (completed)="onStepCompleted(2)"
          (feedbackRequested)="onFeedbackRequested('visit_outcomes', $event)"
        />
      }
      @case ('form') {
        <ws-qualification-form
          [callOutcomes]="suggestions?.call_outcomes"
          [customProperties]="suggestions?.custom_contact_properties"
          [activeAccount]="activeAccount"
          (completed)="onStepCompleted(3)"
          (feedbackRequested)="onFeedbackRequested('custom_contact_properties', $event)"
        />
      }
      @case ('dealStages') {
        <ws-deal-stages
          [suggestions]="suggestions?.deal_stages"
          [activeAccount]="activeAccount"
          (completed)="onStepCompleted(4)"
          (feedbackRequested)="onFeedbackRequested('deal_stages', $event)"
        />
      }
      @case ('products') {
        <ws-products
          [suggestions]="suggestions?.products"
          [activeAccount]="activeAccount"
          (completed)="onStepCompleted(5)"
          (feedbackRequested)="onFeedbackRequested('products', $event)"
        />
      }
      @case ('team') {
        <ws-invite-team
          [activeAccount]="activeAccount"
          (completed)="onTeamStepCompleted($event)"
          (stepReady)="onTeamStepReady()"
        />
      }
      @case ('leads') {
        <ws-import-leads
          [activeAccount]="activeAccount"
          [invitedUserIds]="invitedUserIds"
          [businessDescription]="businessDescription"
          [extractedData]="extractedData"
          (completed)="onStepCompleted(7)"
        />
      }
      @case ('finish') {
        <ws-finish
          [stepStates]="stepStates"
          [steps]="steps"
          [invitedUserIds]="invitedUserIds"
          (reviewStep)="goToStep($event)"
          (finished)="onFinish()"
        />
      }
    }

    <!-- Bottom navigation -->
    @if (currentStepKey !== 'account' && currentStepKey !== 'finish') {
      <div class="ws-nav-bar">
        <button class="btn btn-outline-secondary" (click)="previousStep()" [disabled]="currentStep === 0">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <div class="ws-nav-right">
          @if (canSkipCurrentStep()) {
            <button class="btn btn-link text-muted" (click)="skipStep()">
              Skip this step
            </button>
          }
        </div>
      </div>
    }
  </div>
</div>

<!-- Sticky Footer — Book a Demo -->
<div class="ws-sticky-footer">
  <span>Need assistance in setting up trial or a product demo?</span>
  <a href="javascript:void(0)" (click)="openCalendly()">Book a slot</a>
</div>
